import { useAccount } from "@Light/components/page/account";
import { ButtonFooter } from "@Light/components/page/ButtonFooter";
import {
  useHeaderBackNavigate,
  useHeaderTitle,
} from "@Light/components/page/header/context";
import { MutationButton } from "@Light/components/page/MutationButton";
import { useScaffold } from "@Light/scaffold";
import { useLight } from "@Light/services/light";
import type { AccountUpdateRequest } from "@Light/services/lightTypes";
import { useMutation } from "@Light/utils/mutation";
import React from "react";
import { useForm, useWatch } from "react-hook-form";
import { Navigate } from "react-router-dom";

const BACK_ROUTE = "/account/details/";

type UpdateNameForm = {
  first_name: string;
  last_name: string;
};

export type UpdateNameProps = {};

export const UpdateName: React.FC<UpdateNameProps> = ({}) => {
  const scaffold = useScaffold();
  const { useUpdateAccountMutation } = useLight();
  const account = useAccount();
  const updateAccount = useMutation<AccountUpdateRequest>(
    useUpdateAccountMutation,
  );
  const { register, handleSubmit, control } = useForm<UpdateNameForm>();
  const first_name = useWatch({ control, name: "first_name" });
  const last_name = useWatch({ control, name: "last_name" });
  const disabled =
    account.first_name === first_name && account.last_name === last_name;

  useHeaderTitle("Update your name");
  useHeaderBackNavigate(BACK_ROUTE);

  const mutate = handleSubmit(({ first_name, last_name }) => {
    updateAccount.mutate({ first_name, last_name });
  });

  if (updateAccount.isSuccess) {
    return <Navigate to={BACK_ROUTE} />;
  }

  const PageBody = scaffold.page.pageBody;
  const Field = scaffold.system.field;
  const Label = scaffold.system.label;
  const Input = scaffold.system.input;
  return (
    <form onSubmit={mutate}>
      <PageBody
        footerAfterContent={true}
        footer={
          <ButtonFooter>
            <MutationButton
              size="lg"
              mutate={mutate}
              mutation={updateAccount}
              mutateButtonText="Confirm"
              errorMessage="Error updating your name. Please try again later."
              disabled={disabled}
            />
          </ButtonFooter>
        }
      >
        <div className="flex flex-col gap-4">
          <Field>
            <Label>First name</Label>
            <Input
              {...register("first_name", {
                required: true,
                value: account.first_name,
              })}
            />
          </Field>
          <Field>
            <Label>Last name</Label>
            <Input
              {...register("last_name", {
                required: true,
                value: account.last_name,
              })}
            />
          </Field>
        </div>
      </PageBody>
    </form>
  );
};
